import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, Phone, Mail, User, LogOut } from 'lucide-react';
import { useAuthContext } from '../hooks/useAuth';
import AuthModal from './auth/AuthModal';
import toast from 'react-hot-toast';

interface NavItem {
  title: string;
  items: string[];
}

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, createTemporaryAccount } = useAuthContext();

  const navItems: NavItem[] = [
    {
      title: '产品',
      items: ['云超算标准版', '云超算企业版', '云超算专享版', '云超算私有版', 'ChainCloud Studio']
    },
    {
      title: '解决方案',
      items: ['生物医药', '汽车制造', '材料科学', '金融计算', '科研院所']
    },
    {
      title: '案例',
      items: ['客户案例', '技术白皮书', '解决方案']
    },
    {
      title: '支持',
      items: ['技术文档', '帮助中心', '联系支持']
    }
  ];

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLinkClick = (item: string) => {
    console.log('导航到:', item);
    setIsMenuOpen(false);
  };

  const handleFreeTrial = async () => {
    if (isAuthenticated) {
      // 如果已登录，直接导航到控制台
      navigate('/console');
    } else {
      try {
        // 创建临时账号
        await createTemporaryAccount();
        // 创建成功后导航到控制台
        navigate('/console');
      } catch (error) {
        console.error('创建临时账号失败:', error);
        toast.error('创建临时账号失败，请稍后重试');
      }
    }
  };

  const handleLogin = () => {
    setAuthMode('login');
    setAuthModalOpen(true);
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200/50 transition-all duration-300">
        <div className="container-responsive">
          <div className="flex items-center justify-between py-3 lg:py-4 xl:py-5 2xl:py-6 portrait:py-2">
            {/* Logo */}
            <div className="logo">
              <h2 className="text-lg lg:text-xl xl:text-2xl 2xl:text-3xl portrait:text-base font-bold gradient-text">
                ChainCloud云超算
              </h2>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8 2xl:space-x-10 portrait:space-x-4">
              {navItems.map((item) => (
                <div key={item.title} className="relative group">
                  <button className="text-sm lg:text-base xl:text-lg 2xl:text-xl portrait:text-sm text-gray-700 hover:text-primary-600 transition-colors duration-300 py-2 lg:py-3 portrait:py-1 font-medium">
                    {item.title}
                  </button>
                  
                  {/* Dropdown */}
                  <div className="absolute top-full left-0 min-w-[200px] lg:min-w-[240px] xl:min-w-[280px] 2xl:min-w-[320px] portrait:min-w-[180px] bg-white rounded-lg lg:rounded-xl xl:rounded-2xl portrait:rounded-lg shadow-xl border border-gray-100 py-2 lg:py-3 xl:py-4 portrait:py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transform translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                    {item.items.map((subItem) => (
                      <button
                        key={subItem}
                        onClick={() => handleLinkClick(subItem)}
                        className="block w-full text-left px-3 lg:px-4 xl:px-6 2xl:px-8 portrait:px-3 py-2 lg:py-3 xl:py-4 portrait:py-2 text-xs lg:text-sm xl:text-base 2xl:text-lg portrait:text-xs text-gray-600 hover:text-primary-600 hover:bg-gray-50 transition-all duration-200 border-l-3 border-transparent hover:border-primary-600"
                      >
                        {subItem}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </nav>

            {/* Mobile Menu Button & Actions Container */}
            <div className="flex items-center space-x-3 lg:space-x-4 xl:space-x-6 2xl:space-x-8 portrait:space-x-2">
              {/* Mobile Menu Button */}
              <button
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={handleMenuToggle}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? (
                  <X className="w-5 h-5 text-gray-700" />
                ) : (
                  <Menu className="w-5 h-5 text-gray-700" />
                )}
              </button>

              {/* Contact Info & Actions */}
              <div className="hidden xl:flex items-center space-x-4 2xl:space-x-6 portrait:space-x-3 text-xs lg:text-sm xl:text-base 2xl:text-lg portrait:text-xs text-gray-600">
                <div className="flex items-center space-x-1 lg:space-x-2 portrait:space-x-1">
                  <Phone className="w-3 h-3 lg:w-4 lg:h-4 xl:w-5 xl:h-5 portrait:w-3 portrait:h-3" />
                  <span>400-123-4567</span>
                </div>
                <div className="flex items-center space-x-1 lg:space-x-2 portrait:space-x-1">
                  <Mail className="w-3 h-3 lg:w-4 lg:h-4 xl:w-5 xl:h-5 portrait:w-3 portrait:h-3" />
                  <span>contact@chaincloud.com</span>
                </div>
              </div>
              
              {isAuthenticated ? (
                <div className="flex items-center space-x-2 lg:space-x-3 xl:space-x-4 portrait:space-x-2">
                  {/* 用户信息 */}
                  <div className="hidden sm:flex items-center space-x-1 lg:space-x-2 portrait:space-x-1">
                    <div className="w-6 h-6 lg:w-7 lg:h-7 xl:w-8 xl:h-8 portrait:w-5 portrait:h-5 bg-blue-500 rounded-full flex items-center justify-center">
                      {user?.avatar ? (
                        <img src={user.avatar} alt={user.username} className="w-6 h-6 lg:w-7 lg:h-7 xl:w-8 xl:h-8 portrait:w-5 portrait:h-5 rounded-full" />
                      ) : (
                        <User className="w-3 h-3 lg:w-4 lg:h-4 portrait:w-2 portrait:h-2 text-white" />
                      )}
                    </div>
                    <span className="text-xs lg:text-sm xl:text-base portrait:text-xs font-medium text-gray-700">{user?.username}</span>
                  </div>
                  
                  {/* 控制台按钮 */}
                  <button 
                    className="px-3 lg:px-4 xl:px-6 portrait:px-2 py-1.5 lg:py-2 xl:py-2.5 portrait:py-1 text-xs lg:text-sm xl:text-base portrait:text-xs font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg xl:rounded-xl portrait:rounded-md hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                    onClick={() => navigate('/console')}
                  >
                    控制台
                  </button>
                  
                  {/* 退出按钮 */}
                  <button 
                    className="hidden sm:block p-1.5 lg:p-2 portrait:p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                    onClick={handleLogout}
                    title="退出登录"
                  >
                    <LogOut className="w-3 h-3 lg:w-4 lg:h-4 portrait:w-3 portrait:h-3" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-2 lg:space-x-3 xl:space-x-4 portrait:space-x-2">
                  <button 
                    className="hidden sm:block text-xs lg:text-sm xl:text-base portrait:text-xs text-gray-700 hover:text-blue-600 font-medium transition-colors"
                    onClick={handleLogin}
                  >
                    登录
                  </button>
                  <button 
                    className="px-3 lg:px-4 xl:px-6 portrait:px-2 py-1.5 lg:py-2 xl:py-2.5 portrait:py-1 text-xs lg:text-sm xl:text-base portrait:text-xs font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg xl:rounded-xl portrait:rounded-md hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                    onClick={handleFreeTrial}
                  >
                    免费试用
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {isMenuOpen && (
            <div className="lg:hidden bg-white border-t border-gray-200 py-4">
              <nav className="space-y-4">
                {navItems.map((item) => (
                  <div key={item.title} className="space-y-2">
                    <div className="px-4 py-2 text-sm font-medium text-gray-900 border-b border-gray-100">
                      {item.title}
                    </div>
                    <div className="space-y-1">
                      {item.items.map((subItem) => (
                        <button
                          key={subItem}
                          onClick={() => handleLinkClick(subItem)}
                          className="block w-full text-left px-6 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 transition-colors"
                        >
                          {subItem}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                
                {/* Mobile Contact Info */}
                <div className="px-4 py-3 border-t border-gray-200 space-y-2">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Phone className="w-4 h-4" />
                    <span>400-123-4567</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Mail className="w-4 h-4" />
                    <span>contact@chaincloud.com</span>
                  </div>
                </div>

                {/* Mobile User Actions */}
                {isAuthenticated && (
                  <div className="px-4 py-3 border-t border-gray-200 space-y-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                        {user?.avatar ? (
                          <img src={user.avatar} alt={user.username} className="w-8 h-8 rounded-full" />
                        ) : (
                          <User className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-700">{user?.username}</span>
                    </div>
                    <button 
                      className="flex items-center space-x-2 text-sm text-red-600 hover:text-red-700"
                      onClick={handleLogout}
                    >
                      <LogOut className="w-4 h-4" />
                      <span>退出登录</span>
                    </button>
                  </div>
                )}

                {!isAuthenticated && (
                  <div className="px-4 py-3 border-t border-gray-200">
                    <button 
                      className="block w-full text-left py-2 text-sm text-gray-700 hover:text-blue-600 font-medium transition-colors"
                      onClick={handleLogin}
                    >
                      登录
                    </button>
                  </div>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* 认证模态框 */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        defaultMode={authMode}
      />
    </>
  );
};

export default Header; 